/**
 * Amaron Performance Dashboard config.
 *
 * ─── REMAINING GAPS (still not in dataset) ──────────────────────────────────
 * • Service Delivered stage counts
 *   — Contacted / Connected / Valid / Interested / NR stage counts are derived from Disposition filters.
 * • Agent FRT is available in Agent-wise Performance; the full SLA and Response Tracking section is not implemented.
 * • Revenue / AOV — "Service Revenue and AOV" widget is not implementable.
 * • L1 / L2 / L3 / L4 as separate dimension columns — only unified Disposition.
 * ────────────────────────────────────────────────────────────────────────────
 */

import type {QueryClient} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {
    fetchAnalyticsData,
    getDataset,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-api";
import {DASHBOARD_DATASET_KEYS_BY_WORKSPACE} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-constants";
import {resolveDashboardDatasetsAndColumns} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-helpers";
import {
    DashboardRowType,
    type DashboardConfig,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-types";
import {
    DatasetColumnDataType,
    DatasetColumnRole,
    FilterType,
    TimeGranularity,
    type Dataset,
    type Filter,
} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {
    AnalyticsQueryVariant,
    type DataResolverProps,
    type RawData,
} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {GraphBaseColor} from "~/utilities/analytics/graphColors";
import {WidgetType, type Widget} from "~/components/analyticsWidgets/widgetTypes/widget";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {dataResult, errorResult} from "@vesta/error-handling-remix/result";
import {getAgentDashboardFrt} from "~/utilities/apis/agentDashboardFrtApi";
import {getTimeZonesWithCaching} from "~/utilities/queries";
import {
    AmaronRoles,
    AmaronServiceCategoryEnum,
    getAmaronDispositionIdsForLevels,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    AMARON_BOOKING_TIME_SLOT_HEATMAP_COLUMN_KEYS,
    AMARON_CREATED_AT_TWO_HOUR_HEATMAP_COLUMN_KEYS,
    formatAmaronBookingTimeSlotHeatMapColumnKey,
    formatAmaronCreatedAtTwoHourHeatMapColumnKey,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-dashboardConfig/amaronBookingSlotHeatMapFormatters";

// ─── Chart palette ───────────────────────────────────────────────────────────

const AMARON_FUNNEL_BASE_COLOR = GraphBaseColor.deepMoss;
const AMARON_HEATMAP_WEEKDAY_KEYS: ReadonlyArray<string> = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];
const AMARON_HEATMAP_WEEKDAY_DIMENSION_ID = uniqueUuid`ab450002-4500-4450-8450-450045004502`;
const AMARON_HEATMAP_CREATED_AT_SLOT_DIMENSION_ID = uniqueUuid`ab450003-4500-4450-8450-450045004503`;
const AMARON_HEATMAP_BOOKING_SLOT_DIMENSION_ID = uniqueUuid`ab450005-4500-4450-8450-450045004505`;

function amaronDateOnlyLabel(value: string): string {
    const parsed = DateTime.fromISO(value, {setZone: true});
    if (parsed.isValid === true) {
        return parsed.toISODate();
    }
    return value.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? value;
}

function amaronPercentage(numerator: number, denominator: number): number {
    return denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : 0;
}

function sumRawMetricValue(rawData: RawData, metricId: Uuid): number {
    const metricColumn = rawData.columnMetadata.find((column) => column.id === metricId);
    if (metricColumn === undefined) {
        return 0;
    }

    let total = 0;
    for (const row of rawData.dataPoints) {
        const value = row[metricColumn.columnIndex]?.value;
        if (typeof value === "number" && Number.isFinite(value)) {
            total += value;
        }
    }
    return total;
}

function readRawGroupedMetricValues(rawData: RawData, dimensionId: Uuid, metricId: Uuid): Map<string, number> {
    const dimensionColumn = rawData.columnMetadata.find((column) => column.id === dimensionId);
    const metricColumn = rawData.columnMetadata.find((column) => column.id === metricId);
    const result = new Map<string, number>();
    if (dimensionColumn === undefined || metricColumn === undefined) {
        return result;
    }

    for (const row of rawData.dataPoints) {
        const dimensionValue = row[dimensionColumn.columnIndex]?.value;
        const metricValue = row[metricColumn.columnIndex]?.value;
        const key =
            dimensionValue === null || dimensionValue === undefined || String(dimensionValue).trim() === ""
                ? "Others"
                : String(dimensionValue);
        const numericValue = typeof metricValue === "number" && Number.isFinite(metricValue) ? metricValue : 0;
        result.set(key, (result.get(key) ?? 0) + numericValue);
    }

    return result;
}

/*
const AMARON_DONUT_SLICE_COLORS: ReadonlyArray<string> = [
    getBaseColorHex(GraphBaseColor.dustyCoral),
    getBaseColorHex(GraphBaseColor.softTerracotta),
    getBaseColorHex(GraphBaseColor.skyTeal),
    getBaseColorHex(GraphBaseColor.heatherPurple),
    getBaseColorHex(GraphBaseColor.corporateBlue),
    getBaseColorHex(GraphBaseColor.jadeEmerald),
    getBaseColorHex(GraphBaseColor.periwinkle),
    getBaseColorHex(GraphBaseColor.mutedRose),
    getBaseColorHex(GraphBaseColor.deepMoss),
    getBaseColorHex(GraphBaseColor.neutral),
];
*/

/*
const AMARON_SERIES_COLORS = {
    totalLeads: {
        primary: getBaseColorHex(GraphBaseColor.corporateBlue),
        secondary: getBaseColorHex(GraphBaseColor.softTerracotta),
    },
    enquiries: {
        primary: getBaseColorHex(GraphBaseColor.skyTeal),
        secondary: getBaseColorHex(GraphBaseColor.softTerracotta),
    },
    calls: {
        primary: getBaseColorHex(GraphBaseColor.heatherPurple),
        secondary: getBaseColorHex(GraphBaseColor.softTerracotta),
    },
    serviceBooked: {
        primary: getBaseColorHex(GraphBaseColor.jadeEmerald),
        secondary: getBaseColorHex(GraphBaseColor.softTerracotta),
    },
    conversionRate: {
        primary: getBaseColorHex(GraphBaseColor.dustyCoral),
        secondary: getBaseColorHex(GraphBaseColor.softTerracotta),
    },
    connectivity: {
        primary: getBaseColorHex(GraphBaseColor.periwinkle),
        secondary: getBaseColorHex(GraphBaseColor.softTerracotta),
    },
};
*/

// ─── Config ──────────────────────────────────────────────────────────────────

export function getAmaronDashboardConfig({
    datasets,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    queryClient,
    users,
    userTemplates,
    platformUserDetailsByUserId,
    ouTemplates,
    timeZone,
    startTime,
    endTime,
    eventContainerDispositions,
}: {
    datasets: Array<Dataset>;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    queryClient: QueryClient;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    platformUserDetailsByUserId: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    ouTemplates: Array<OrganizationalUnitTemplate>;
    timeZone: Uuid;
    startTime: Iso8601DateTime;
    endTime: Iso8601DateTime;
    eventContainerDispositions: Array<{id: Uuid; displayName: string}>;
}): DashboardConfig {
    const dashboardData = resolveDashboardDatasetsAndColumns(datasets, DASHBOARD_DATASET_KEYS_BY_WORKSPACE.amaron);
    const amaronPerformanceDataset = dashboardData.datasets.amaronPerformanceDataset;
    const amaronDailyPerformanceDataset = dashboardData.datasets.amaronDailyPerformanceDataset;
    const AMARON = dashboardData.columnIds.amaronPerformanceDataset;
    const AMARON_DAILY = dashboardData.columnIds.amaronDailyPerformanceDataset;
    const AMARON_DISPOSITION_TILE_IDS = {
        newLead: getAmaronDispositionIdsForLevels({l1Values: ["new-lead"]}),
        connected: getAmaronDispositionIdsForLevels({l1Values: ["connected"]}),
        notConnected: getAmaronDispositionIdsForLevels({l1Values: ["not-connected"]}),
        wrongNumber: getAmaronDispositionIdsForLevels({l1Values: ["wrong-number"]}),
        validLead: getAmaronDispositionIdsForLevels({l2Values: ["valid"]}),
        callBackLater: getAmaronDispositionIdsForLevels({l2Values: ["call-back-later"]}),
        invalidLead: getAmaronDispositionIdsForLevels({l2Values: ["invalid"]}),
        notReachable: getAmaronDispositionIdsForLevels({l2Values: ["nr1", "nr2", "nr3", "nr4", "nr5", "nr6"]}),
        interested: getAmaronDispositionIdsForLevels({l3Values: ["interested"]}),
        notInterested: getAmaronDispositionIdsForLevels({l3Values: ["not-interested"]}),
        unserviceable: getAmaronDispositionIdsForLevels({l3Values: ["unserviceable"]}),
        callHangup: getAmaronDispositionIdsForLevels({l2Values: ["call-hung-up"]}),
        didNotFill: getAmaronDispositionIdsForLevels({l3Values: ["did-not-fill"]}),
        filledByMistake: getAmaronDispositionIdsForLevels({l3Values: ["filled-by-mistake"]}),
        nr1: getAmaronDispositionIdsForLevels({l2Values: ["nr1"]}),
        nr2: getAmaronDispositionIdsForLevels({l2Values: ["nr2"]}),
        nr3: getAmaronDispositionIdsForLevels({l2Values: ["nr3"]}),
        nr4: getAmaronDispositionIdsForLevels({l2Values: ["nr4"]}),
        nr5: getAmaronDispositionIdsForLevels({l2Values: ["nr5"]}),
        nr6: getAmaronDispositionIdsForLevels({l2Values: ["nr6"]}),
        priceIssue: getAmaronDispositionIdsForLevels({l4Values: ["price-issue"]}),
        serviceNotDue: getAmaronDispositionIdsForLevels({l4Values: ["service-not-due"]}),
        oemPreferred: getAmaronDispositionIdsForLevels({l4Values: ["oem-preferred"]}),
        newCar: getAmaronDispositionIdsForLevels({l4Values: ["new-car"]}),
        noRequirementShared: getAmaronDispositionIdsForLevels({l4Values: ["no-requirement-shared"]}),
        generalQuery: getAmaronDispositionIdsForLevels({l4Values: ["general-query"]}),
        areaNotCovered: getAmaronDispositionIdsForLevels({l4Values: ["area-not-covered"]}),
        carNotSupported: getAmaronDispositionIdsForLevels({l4Values: ["car-not-supported"]}),
        immediateService: getAmaronDispositionIdsForLevels({l4Values: ["immediate-service"]}),
        rsaRequest: getAmaronDispositionIdsForLevels({l4Values: ["rsa-request"]}),
        noCarDetails: getAmaronDispositionIdsForLevels({l4Values: ["no-car-details"]}),
        followUp: getAmaronDispositionIdsForLevels({l4Values: ["follow-up"]}),
        needMoreInformation: getAmaronDispositionIdsForLevels({l4Values: ["need-more-information"]}),
        booked: getAmaronDispositionIdsForLevels({l4Values: ["booked"]}),
        other: getAmaronDispositionIdsForLevels({l4Values: ["other"]}),
        notInterestedReasons: getAmaronDispositionIdsForLevels({l3Values: ["not-interested"]}),
        unserviceableReasons: getAmaronDispositionIdsForLevels({l3Values: ["unserviceable"]}),
    };

    const partialGetDataset = (datasetId: Uuid) => getDataset(datasetId, intellsysBaseUrl, accessToken, workspaceId);

    const partialFetchData = (dataset: Dataset, props: DataResolverProps) =>
        fetchAnalyticsData(
            intellsysBaseUrl,
            accessToken,
            workspaceId,
            queryClient,
            users,
            userTemplates,
            ouTemplates,
            dashboardData,
            dataset,
            props,
            eventContainerDispositions,
        );

    // ── Widget factories (performance dataset) ───────────────────────────────

    const makeNumberTileWidget = (
        id: Uuid,
        title: string | null,
        metrics: Uuid[],
        valueSuffix: string = "",
        decimalPlaces: number = 0,
        editTimeFilters: Array<Filter> = [],
    ): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: amaronPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        externalFilters: [],
        editTimeFilters,
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix,
    });

    const makeDispositionTile = (id: Uuid, title: string | null, dispositionIds: Array<Uuid>): Widget => ({
        ...makeNumberTileWidget(id, title, [AMARON.totalLeads]),
        editTimeFilters: [
            {
                type: FilterType.stringIn,
                attribute: AMARON.disposition,
                values: dispositionIds,
            } satisfies Filter,
        ],
    });

    const makeChartWidget = (
        id: Uuid,
        type:
            | WidgetType.pieChart
            | WidgetType.donutChart
            | WidgetType.multiBarGraph
            | WidgetType.stackedBarGraph
            | WidgetType.horizontalBarGraph
            | WidgetType.lineGraph
            | WidgetType.areaGraph
            | WidgetType.comboChart
            | WidgetType.table,
        title: string | null,
        metrics: Uuid[],
        dimensions: Uuid[],
        queryVariant: AnalyticsQueryVariant = AnalyticsQueryVariant.periodicAsNonTimeSeries,
        options?: {
            dismissChartHoverTooltipOnPointerLeave?: boolean;
            chartColorPalette?: ReadonlyArray<string>;
        },
    ): Widget => ({
        id,
        type,
        datasetId: amaronPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions,
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant,
        dismissChartHoverTooltipOnPointerLeave: options?.dismissChartHoverTooltipOnPointerLeave,
        chartColorPalette: options?.chartColorPalette,
    });

    // ── Widget factories (daily dataset) ─────────────────────────────────────

    const makeDailyChartWidget = (
        id: Uuid,
        type:
            | WidgetType.pieChart
            | WidgetType.donutChart
            | WidgetType.multiBarGraph
            | WidgetType.stackedBarGraph
            | WidgetType.horizontalBarGraph
            | WidgetType.lineGraph
            | WidgetType.areaGraph
            | WidgetType.comboChart
            | WidgetType.table,
        title: string | null,
        metrics: Uuid[],
        dimensions: Uuid[],
        queryVariant: AnalyticsQueryVariant = AnalyticsQueryVariant.periodicAsPeriodic,
        options?: {
            dismissChartHoverTooltipOnPointerLeave?: boolean;
            chartColorPalette?: ReadonlyArray<string>;
        },
    ): Widget => ({
        id,
        type,
        datasetId: amaronDailyPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions,
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant,
        dismissChartHoverTooltipOnPointerLeave: options?.dismissChartHoverTooltipOnPointerLeave,
        chartColorPalette: options?.chartColorPalette,
    });

    /*
    const makeFunnelWidget = ({
        id,
        title,
        metrics,
        showValues = true,
        showConversionRate = false,
        showDropOffRate = false,
    }: {
        id: Uuid;
        title: string | null;
        metrics: Uuid[];
        showValues?: boolean;
        showConversionRate?: boolean;
        showDropOffRate?: boolean;
    }): Widget => ({
        id,
        type: WidgetType.funnelChart,
        datasetId: amaronPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        showValues,
        showConversionRate,
        showDropOffRate,
        funnelBaseColor: AMARON_FUNNEL_BASE_COLOR,
    });
    */

    const makeDispositionFilteredHorizontalBarGraphWidget = ({
        id,
        title,
        stages,
    }: {
        id: Uuid;
        title: string | null;
        stages: ReadonlyArray<{
            metricId: Uuid;
            label: string;
            dispositionIds: Array<Uuid>;
        }>;
    }): Widget => ({
        id,
        type: WidgetType.horizontalBarGraph,
        datasetId: amaronPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: stages.map((stage) => stage.metricId),
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (dataset, props) => {
            const metricTemplate = props.metrics[0];
            if (metricTemplate === undefined) {
                return dataResult({
                    columnMetadata: [],
                    dataPoints: [],
                } satisfies RawData);
            }

            const stageValues: Array<number> = [];
            for (const stage of stages) {
                const dispositionFilter: Filter = {
                    type: FilterType.stringIn,
                    attribute: AMARON.disposition,
                    values: stage.dispositionIds,
                };

                const result = await partialFetchData(dataset, {
                    ...props,
                    dimensions: [],
                    metrics: [
                        {
                            ...metricTemplate,
                            id: AMARON.totalLeads,
                        },
                    ],
                    filters: {
                        type: FilterType.and,
                        predicates: [props.filters, dispositionFilter],
                    },
                } as DataResolverProps);

                if (result.success !== true) {
                    return result;
                }

                stageValues.push(sumRawMetricValue(result.data, AMARON.totalLeads));
            }

            return dataResult({
                columnMetadata: stages.map((stage, columnIndex) => ({
                    id: stage.metricId,
                    columnIndex,
                    displayName: stage.label,
                    dataType: DatasetColumnDataType.number,
                    role: DatasetColumnRole.metric,
                })),
                dataPoints: [stageValues.map((value) => ({value}))],
            } satisfies RawData);
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
    });

    const makeMetricAndDispositionFunnelWidget = ({
        id,
        title,
        stages,
        showValues = true,
        showConversionRate = false,
        showDropOffRate = false,
        funnelLayout = "inline",
    }: {
        id: Uuid;
        title: string | null;
        stages: ReadonlyArray<{
            metricId: Uuid;
            label: string;
            sourceMetricId: Uuid;
            dispositionIds?: Array<Uuid>;
            excludedDispositionIds?: Array<Uuid>;
            statusIds?: Array<Uuid>;
            categoryIds?: Array<Uuid>;
        }>;
        showValues?: boolean;
        showConversionRate?: boolean;
        showDropOffRate?: boolean;
        funnelLayout?: "inline" | "labeledLeft";
    }): Widget => ({
        id,
        type: WidgetType.funnelChart,
        datasetId: amaronPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: stages.map((stage) => stage.metricId),
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (dataset, props) => {
            const metricTemplate = props.metrics[0];
            if (metricTemplate === undefined) {
                return dataResult({
                    columnMetadata: [],
                    dataPoints: [],
                } satisfies RawData);
            }

            const stageValues: Array<number> = [];
            for (const stage of stages) {
                const buildStageFilters = (dispositionIds?: Array<Uuid>): Filter => {
                    const filterPredicates: Array<Filter> = [props.filters];
                    if (dispositionIds !== undefined) {
                        filterPredicates.push({
                            type: FilterType.stringIn,
                            attribute: AMARON.disposition,
                            values: dispositionIds,
                        });
                    }
                    if (stage.statusIds !== undefined) {
                        filterPredicates.push({
                            type: FilterType.stringIn,
                            attribute: AMARON.status,
                            values: stage.statusIds,
                        });
                    }
                    if (stage.categoryIds !== undefined) {
                        filterPredicates.push({
                            type: FilterType.stringIn,
                            attribute: AMARON.category,
                            values: stage.categoryIds,
                        });
                    }
                    return filterPredicates.length === 1
                        ? props.filters
                        : {
                              type: FilterType.and,
                              predicates: filterPredicates,
                          };
                };

                const fetchStageValue = async (filters: Filter) => {
                    const result = await partialFetchData(dataset, {
                        ...props,
                        dimensions: [],
                        metrics: [
                            {
                                ...metricTemplate,
                                id: stage.sourceMetricId,
                            },
                        ],
                        filters,
                    } as DataResolverProps);

                    if (result.success !== true) {
                        return result;
                    }

                    return dataResult(sumRawMetricValue(result.data, stage.sourceMetricId));
                };

                const includedResult = await fetchStageValue(buildStageFilters(stage.dispositionIds));
                if (includedResult.success !== true) {
                    return includedResult;
                }

                let stageValue = includedResult.data;
                if (stage.excludedDispositionIds !== undefined) {
                    const excludedResult = await fetchStageValue(buildStageFilters(stage.excludedDispositionIds));
                    if (excludedResult.success !== true) {
                        return excludedResult;
                    }
                    stageValue = Math.max(0, stageValue - excludedResult.data);
                }

                stageValues.push(stageValue);
            }

            return dataResult({
                columnMetadata: stages.map((stage, columnIndex) => ({
                    id: stage.metricId,
                    columnIndex,
                    displayName: stage.label,
                    dataType: DatasetColumnDataType.number,
                    role: DatasetColumnRole.metric,
                })),
                dataPoints: [stageValues.map((value) => ({value}))],
            } satisfies RawData);
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        showValues,
        showConversionRate,
        showDropOffRate,
        funnelLayout,
        funnelBaseColor: AMARON_FUNNEL_BASE_COLOR,
    });

    const makeAgentPerformanceTableWidget = (id: Uuid, title: string | null): Widget => {
        const agentFilter: Filter = {
            type: FilterType.stringIn,
            attribute: AMARON.userTemplate,
            values: [AmaronRoles.Agent],
        };

        // TODO: Please add these metrics on backend or remove this widget, frontend synthesis of data is not a good idea
        // These are hardcoded just for state management consistency
        // else these ids are not important for the widget itself, can be changed to anything
        const stageMetricIds = {
            totalLeads: uniqueUuid`c8562a0b-d84f-4cdf-adfb-43d71257003d`,
            pendingLeads: uniqueUuid`c19944f9-b48a-4355-8172-5b2aee03d2a9`,
            contacted: uniqueUuid`9224512a-8aeb-4cbc-a3fd-c6132613b0dd`,
            contactedPct: uniqueUuid`a9c33065-6c90-43e0-971e-a0c73e537b4f`,
            connected: uniqueUuid`b5b15a93-54d3-453c-923e-1b77cdb2359b`,
            connectivityPct: uniqueUuid`fef3de34-5064-4d37-a486-d0eb9b5a471f`,
            interested: uniqueUuid`fe63c4eb-5b63-4d18-95e9-23499e6e25b0`,
            bookings: uniqueUuid`3e937b96-31ef-4552-a44d-53dcdfa4bee0`,
            delivered: uniqueUuid`ad0f6ce9-3503-4faa-90e2-056a7859abbf`,
            agentFrt: uniqueUuid`9cab39b7-3762-46cc-bd63-7365ac8bd8ad`,
        };
        const stages: ReadonlyArray<{
            metricId: Uuid;
            label: string;
            sourceMetricId: Uuid;
            filters?: Array<Filter>;
            subtractFromMetricId?: Uuid;
        }> = [
            {
                metricId: stageMetricIds.totalLeads,
                label: "Total Leads",
                sourceMetricId: AMARON.totalLeads,
            },
            {
                metricId: stageMetricIds.pendingLeads,
                label: "Pending Leads",
                sourceMetricId: AMARON.totalLeads,
                filters: [
                    {
                        type: FilterType.stringIn,
                        attribute: AMARON.disposition,
                        values: AMARON_DISPOSITION_TILE_IDS.newLead,
                    },
                ],
            },
            {
                metricId: stageMetricIds.contacted,
                label: "Contacted",
                sourceMetricId: AMARON.totalLeads,
                filters: [
                    {
                        type: FilterType.stringIn,
                        attribute: AMARON.disposition,
                        values: AMARON_DISPOSITION_TILE_IDS.newLead,
                    },
                ],
                subtractFromMetricId: stageMetricIds.totalLeads,
            },
            {
                metricId: stageMetricIds.connected,
                label: "Connected",
                sourceMetricId: AMARON.totalLeads,
                filters: [
                    {
                        type: FilterType.stringIn,
                        attribute: AMARON.disposition,
                        values: AMARON_DISPOSITION_TILE_IDS.connected,
                    },
                ],
            },
            {
                metricId: stageMetricIds.interested,
                label: "Interested",
                sourceMetricId: AMARON.totalLeads,
                filters: [
                    {
                        type: FilterType.stringIn,
                        attribute: AMARON.disposition,
                        values: AMARON_DISPOSITION_TILE_IDS.interested,
                    },
                ],
            },
            {
                metricId: stageMetricIds.bookings,
                label: "Bookings",
                sourceMetricId: AMARON.serviceBooked,
            },
            {
                metricId: stageMetricIds.delivered,
                label: "Delivered",
                sourceMetricId: AMARON.serviceBooked,
                filters: [
                    {
                        type: FilterType.stringIn,
                        attribute: AMARON.category,
                        values: [AmaronServiceCategoryEnum.DELIVERED as Uuid],
                    },
                ],
            },
        ];

        return {
            id,
            type: WidgetType.table,
            datasetId: amaronPerformanceDataset.id,
            datasetResolver: partialGetDataset,
            title,
            startDate: startTime,
            endDate: endTime,
            dimensions: [AMARON.user],
            metrics: Object.values(stageMetricIds),
            externalFilters: [],
            editTimeFilters: [],
            viewTimeFilters: [],
            sortConfiguration: [],
            dataResolver: async (dataset, props) => {
                const metricTemplate = props.metrics[0];
                if (metricTemplate === undefined) {
                    return dataResult({
                        columnMetadata: [],
                        dataPoints: [],
                    } satisfies RawData);
                }

                const userKeys = new Set<string>();
                const stageValuesByMetricId = new Map<Uuid, Map<string, number>>();

                for (const stage of stages) {
                    const result = await partialFetchData(dataset, {
                        ...props,
                        dimensions: [{id: AMARON.user}],
                        metrics: [
                            {
                                ...metricTemplate,
                                id: stage.sourceMetricId,
                            },
                        ],
                        filters:
                            stage.filters === undefined || stage.filters.length === 0
                                ? {
                                      type: FilterType.and,
                                      predicates: [props.filters, agentFilter],
                                  }
                                : {
                                      type: FilterType.and,
                                      predicates: [props.filters, agentFilter, ...stage.filters],
                                  },
                    } as DataResolverProps);

                    if (result.success !== true) {
                        return result;
                    }

                    const groupedValues = readRawGroupedMetricValues(result.data, AMARON.user, stage.sourceMetricId);
                    let displayedValues = groupedValues;
                    if (stage.subtractFromMetricId !== undefined) {
                        const baseValues =
                            stageValuesByMetricId.get(stage.subtractFromMetricId) ?? new Map<string, number>();
                        const keys = new Set([...baseValues.keys(), ...groupedValues.keys()]);
                        displayedValues = new Map(
                            Array.from(keys, (userKey) => [
                                userKey,
                                Math.max(0, (baseValues.get(userKey) ?? 0) - (groupedValues.get(userKey) ?? 0)),
                            ]),
                        );
                    }
                    stageValuesByMetricId.set(stage.metricId, displayedValues);
                    for (const userKey of displayedValues.keys()) {
                        userKeys.add(userKey);
                    }
                }

                const orderedUserKeys = Array.from(userKeys).sort((a, b) => a.localeCompare(b));
                if (orderedUserKeys.length === 0) {
                    return dataResult({columnMetadata: [], dataPoints: []} satisfies RawData);
                }
                const timeZonesResult = await getTimeZonesWithCaching({intellsysBaseUrl, queryClient});
                if (timeZonesResult.success !== true) {
                    return timeZonesResult;
                }
                const dashboardZone = timeZonesResult.data.find((zone) => zone.id === props.timeZone);
                if (dashboardZone === undefined) {
                    return errorResult(new Error("Agent FRT dashboard timezone not found."));
                }
                const frtResult = await getAgentDashboardFrt({
                    intellsysBaseUrl,
                    accessToken,
                    workspaceId,
                    from: props.from,
                    to: props.to,
                    timezoneOffsetMinutes: dashboardZone.sdtUtcOffsetInMinutes,
                    agentKeys: orderedUserKeys,
                });
                if (frtResult.success !== true) {
                    return frtResult;
                }
                return dataResult({
                    columnMetadata: [
                        {
                            id: AMARON.user,
                            columnIndex: 0,
                            displayName: "Agent",
                            dataType: DatasetColumnDataType.string,
                            role: DatasetColumnRole.dimension,
                        },
                        ...[
                            {id: stageMetricIds.totalLeads, displayName: "Total Leads"},
                            {id: stageMetricIds.pendingLeads, displayName: "Pending Leads"},
                            {id: stageMetricIds.contacted, displayName: "Contacted"},
                            {id: stageMetricIds.contactedPct, displayName: "Contacted (%)"},
                            {id: stageMetricIds.connected, displayName: "Connected"},
                            {id: stageMetricIds.connectivityPct, displayName: "Connectivity (%)"},
                            {id: stageMetricIds.interested, displayName: "Interested"},
                            {id: stageMetricIds.bookings, displayName: "Bookings"},
                            {id: stageMetricIds.delivered, displayName: "Delivered"},
                            {id: stageMetricIds.agentFrt, displayName: "Agent FRT (minutes)"},
                        ].map((column, index) => ({
                            ...column,
                            columnIndex: index + 1,
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        })),
                    ],
                    dataPoints: orderedUserKeys.map((userKey) => {
                        const totalLeads = stageValuesByMetricId.get(stageMetricIds.totalLeads)?.get(userKey) ?? 0;
                        const pendingLeads =
                            stageValuesByMetricId.get(stageMetricIds.pendingLeads)?.get(userKey) ?? 0;
                        const contacted = stageValuesByMetricId.get(stageMetricIds.contacted)?.get(userKey) ?? 0;
                        const connected = stageValuesByMetricId.get(stageMetricIds.connected)?.get(userKey) ?? 0;
                        return [
                            {value: getUserDisplayName(userKey as Uuid, users, userTemplates, platformUserDetailsByUserId)},
                            {value: totalLeads},
                            {value: pendingLeads},
                            {value: contacted},
                            {value: amaronPercentage(contacted, totalLeads)},
                            {value: connected},
                            {value: amaronPercentage(connected, totalLeads)},
                            {value: stageValuesByMetricId.get(stageMetricIds.interested)?.get(userKey) ?? 0},
                            {value: stageValuesByMetricId.get(stageMetricIds.bookings)?.get(userKey) ?? 0},
                            {value: stageValuesByMetricId.get(stageMetricIds.delivered)?.get(userKey) ?? 0},
                            {value: frtResult.data.get(userKey) ?? null},
                        ];
                    }),
                } satisfies RawData);
            },
            timeZone,
            hideDateRangeFilter: true,
            hideKebabMenu: true,
            queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        };
    };

    const makeCreatedAtTwoHourWeekdayHeatMapWidget = ({
        id,
        title,
        valueMetricId,
    }: {
        id: Uuid;
        title: string | null;
        valueMetricId: Uuid;
    }): Widget => ({
        id,
        type: WidgetType.heatMap,
        datasetId: amaronDailyPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [AMARON_HEATMAP_WEEKDAY_DIMENSION_ID, AMARON_HEATMAP_CREATED_AT_SLOT_DIMENSION_ID],
        metrics: [valueMetricId],
        rowDimensionId: AMARON_HEATMAP_WEEKDAY_DIMENSION_ID,
        columnDimensionId: AMARON_HEATMAP_CREATED_AT_SLOT_DIMENSION_ID,
        valueMetricId,
        rowAxisLabel: "Day of Week",
        columnAxisLabel: "Created At 2-Hour Slot",
        colorScheme: "sequentialGreen",
        showCellValues: true,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (dataset, props) => {
            const result = await partialFetchData(dataset, {
                ...props,
                dimensions: [],
                granularity: TimeGranularity.hourly,
            } as DataResolverProps);
            if (result.success !== true) {
                return result;
            }

            const raw = result.data;
            const metricColumn = raw.columnMetadata.find((column) => column.id === valueMetricId);
            const timeColumn = raw.columnMetadata.find((column) => column.role === DatasetColumnRole.dimension);

            if (metricColumn === undefined || timeColumn === undefined) {
                return dataResult(raw);
            }

            const sumBySlotAndWeekday = new Map<string, {slot: string; weekday: string; sum: number}>();

            for (const row of raw.dataPoints) {
                const rawTime = row[timeColumn.columnIndex]?.value;
                const rawMetric = row[metricColumn.columnIndex]?.value;
                const createdAtHour = DateTime.fromISO(String(rawTime ?? ""), {setZone: true});
                if (createdAtHour.isValid !== true) {
                    continue;
                }
                const weekday = createdAtHour.weekdayLong;
                const slot = formatAmaronCreatedAtTwoHourHeatMapColumnKey(createdAtHour.hour);
                const numericValue = typeof rawMetric === "number" && Number.isFinite(rawMetric) ? rawMetric : 0;
                const key = `${slot}\u0000${weekday}`;
                const existing = sumBySlotAndWeekday.get(key);
                if (existing !== undefined) {
                    existing.sum += numericValue;
                } else {
                    sumBySlotAndWeekday.set(key, {slot, weekday, sum: numericValue});
                }
            }

            // Feed the generic heat map with created-at weekdays as rows and two-hour slots as columns.
            const dataPoints: Array<Array<{value: string | number | null}>> = Array.from(
                sumBySlotAndWeekday.values(),
            ).map((entry) => [
                {value: entry.weekday},
                {value: entry.slot},
                {value: entry.sum},
            ]);

            return dataResult({
                columnMetadata: [
                    {
                        id: AMARON_HEATMAP_WEEKDAY_DIMENSION_ID,
                        columnIndex: 0,
                        displayName: "Day of Week",
                        dataType: DatasetColumnDataType.string,
                        role: DatasetColumnRole.dimension,
                    },
                    {
                        id: AMARON_HEATMAP_CREATED_AT_SLOT_DIMENSION_ID,
                        columnIndex: 1,
                        displayName: "Created At 2-Hour Slot",
                        dataType: DatasetColumnDataType.string,
                        role: DatasetColumnRole.dimension,
                    },
                    {
                        ...metricColumn,
                        columnIndex: 2,
                    },
                ],
                dataPoints,
                heatMapFixedRowKeys: AMARON_HEATMAP_WEEKDAY_KEYS,
                heatMapFixedColumnKeys: AMARON_CREATED_AT_TWO_HOUR_HEATMAP_COLUMN_KEYS,
            });
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
    });

    const makeBookingTimeSlotWeekdayHeatMapWidget = ({
        id,
        title,
    }: {
        id: Uuid;
        title: string | null;
    }): Widget => ({
        id,
        type: WidgetType.heatMap,
        datasetId: amaronDailyPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [AMARON_HEATMAP_WEEKDAY_DIMENSION_ID, AMARON_HEATMAP_BOOKING_SLOT_DIMENSION_ID],
        metrics: [AMARON_DAILY.serviceStartedAt],
        rowDimensionId: AMARON_HEATMAP_WEEKDAY_DIMENSION_ID,
        columnDimensionId: AMARON_HEATMAP_BOOKING_SLOT_DIMENSION_ID,
        valueMetricId: AMARON_DAILY.serviceStartedAt,
        rowAxisLabel: "Day of Week",
        columnAxisLabel: "Booking Time Slot",
        colorScheme: "sequentialGreen",
        showCellValues: true,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (dataset, props) => {
            const result = await partialFetchData(dataset, {
                ...props,
                dimensions: [{id: AMARON_DAILY.timeSlot}],
                granularity: TimeGranularity.daily,
            } as DataResolverProps);
            if (result.success !== true) {
                return result;
            }

            const raw = result.data;
            const metricColumn = raw.columnMetadata.find(
                (column) => column.id === AMARON_DAILY.serviceStartedAt,
            );
            const bookingSlotColumn = raw.columnMetadata.find(
                (column) => column.id === AMARON_DAILY.timeSlot,
            );
            const timeColumn = raw.columnMetadata.find(
                (column) =>
                    column.role === DatasetColumnRole.dimension && column.id !== AMARON_DAILY.timeSlot,
            );

            if (metricColumn === undefined || bookingSlotColumn === undefined || timeColumn === undefined) {
                return dataResult(raw);
            }

            const sumBySlotAndWeekday = new Map<string, {slot: string; weekday: string; sum: number}>();
            for (const row of raw.dataPoints) {
                const serviceStartedDay = DateTime.fromISO(String(row[timeColumn.columnIndex]?.value ?? ""), {
                    setZone: true,
                });
                const rawBookingSlot = row[bookingSlotColumn.columnIndex]?.value;
                const slot = formatAmaronBookingTimeSlotHeatMapColumnKey(String(rawBookingSlot ?? ""));
                if (serviceStartedDay.isValid !== true || slot === null) {
                    continue;
                }

                const weekday = serviceStartedDay.weekdayLong;
                const rawMetric = row[metricColumn.columnIndex]?.value;
                const numericValue = typeof rawMetric === "number" && Number.isFinite(rawMetric) ? rawMetric : 0;
                const key = `${slot}\u0000${weekday}`;
                const existing = sumBySlotAndWeekday.get(key);
                if (existing !== undefined) {
                    existing.sum += numericValue;
                } else {
                    sumBySlotAndWeekday.set(key, {slot, weekday, sum: numericValue});
                }
            }

            return dataResult({
                columnMetadata: [
                    {
                        id: AMARON_HEATMAP_WEEKDAY_DIMENSION_ID,
                        columnIndex: 0,
                        displayName: "Day of Week",
                        dataType: DatasetColumnDataType.string,
                        role: DatasetColumnRole.dimension,
                    },
                    {
                        id: AMARON_HEATMAP_BOOKING_SLOT_DIMENSION_ID,
                        columnIndex: 1,
                        displayName: "Booking Time Slot",
                        dataType: DatasetColumnDataType.string,
                        role: DatasetColumnRole.dimension,
                    },
                    {
                        ...metricColumn,
                        columnIndex: 2,
                    },
                ],
                dataPoints: Array.from(sumBySlotAndWeekday.values()).map((entry) => [
                    {value: entry.weekday},
                    {value: entry.slot},
                    {value: entry.sum},
                ]),
                heatMapFixedRowKeys: AMARON_HEATMAP_WEEKDAY_KEYS,
                heatMapFixedColumnKeys: AMARON_BOOKING_TIME_SLOT_HEATMAP_COLUMN_KEYS,
            });
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
    });

    const makeDailyDispositionTrackerTableWidget = (id: Uuid, title: string | null): Widget => {
        const colIds = {
            date: uniqueUuid`ab320201-3202-4320-8320-320032003211`,
            totalLeads: uniqueUuid`ab320202-3202-4320-8320-320032003212`,
            contacted: uniqueUuid`ab320203-3202-4320-8320-320032003213`,
            contactedPct: uniqueUuid`ab320210-3202-4320-8320-320032003220`,
            connected: uniqueUuid`ab320204-3202-4320-8320-320032003214`,
            connectivityPct: uniqueUuid`ab320205-3202-4320-8320-320032003215`,
            interested: uniqueUuid`ab320206-3202-4320-8320-320032003216`,
            booked: uniqueUuid`ab320207-3202-4320-8320-320032003217`,
            delivered: uniqueUuid`ab320209-3202-4320-8320-320032003219`,
            crPct: uniqueUuid`ab320208-3202-4320-8320-320032003218`,
        };

        return {
            id,
            type: WidgetType.table,
            datasetId: amaronDailyPerformanceDataset.id,
            datasetResolver: partialGetDataset,
            title,
            startDate: startTime,
            endDate: endTime,
            dimensions: [],
            metrics: Object.values(colIds),
            externalFilters: [],
            editTimeFilters: [],
            viewTimeFilters: [],
            sortConfiguration: [],
            dataResolver: async (dataset, props) => {
                const metricTemplate = props.metrics[0];
                if (metricTemplate === undefined) {
                    return dataResult({columnMetadata: [], dataPoints: []} satisfies RawData);
                }

                const fetchByDay = async (
                    metricId: Uuid,
                    extraFilter?: Filter,
                ): Promise<Map<string, number>> => {
                    const filters: Filter =
                        extraFilter !== undefined
                            ? {type: FilterType.and, predicates: [props.filters, extraFilter]}
                            : props.filters;
                    const result = await partialFetchData(dataset, {
                        ...props,
                        dimensions: [],
                        metrics: [{...metricTemplate, id: metricId}],
                        filters,
                    } as DataResolverProps);
                    if (result.success !== true) {
                        return new Map();
                    }
                    const timeCol = result.data.columnMetadata.find(
                        (c) => c.role === DatasetColumnRole.dimension,
                    );
                    const metricCol = result.data.columnMetadata.find((c) => c.id === metricId);
                    if (timeCol === undefined || metricCol === undefined) {
                        return new Map();
                    }
                    const map = new Map<string, number>();
                    for (const row of result.data.dataPoints) {
                        const dateKey = String(row[timeCol.columnIndex]?.value ?? "");
                        const val = row[metricCol.columnIndex]?.value;
                        const num =
                            typeof val === "number" && Number.isFinite(val) ? val : 0;
                        map.set(dateKey, (map.get(dateKey) ?? 0) + num);
                    }
                    return map;
                };

                const connectedFilter: Filter = {
                    type: FilterType.stringIn,
                    attribute: AMARON_DAILY.disposition,
                    values: AMARON_DISPOSITION_TILE_IDS.connected,
                };
                const newLeadFilter: Filter = {
                    type: FilterType.stringIn,
                    attribute: AMARON_DAILY.disposition,
                    values: AMARON_DISPOSITION_TILE_IDS.newLead,
                };
                const interestedFilter: Filter = {
                    type: FilterType.stringIn,
                    attribute: AMARON_DAILY.disposition,
                    values: AMARON_DISPOSITION_TILE_IDS.interested,
                };
                const deliveredFilter: Filter = {
                    type: FilterType.stringIn,
                    attribute: AMARON_DAILY.category,
                    values: [AmaronServiceCategoryEnum.DELIVERED as Uuid],
                };

                const totalLeadsMap = await fetchByDay(AMARON_DAILY.totalLeads);
                const newLeadsMap = await fetchByDay(AMARON_DAILY.totalLeads, newLeadFilter);
                const connectedMap = await fetchByDay(AMARON_DAILY.totalLeads, connectedFilter);
                const interestedMap = await fetchByDay(AMARON_DAILY.totalLeads, interestedFilter);
                const bookedMap = await fetchByDay(AMARON_DAILY.serviceBooked);
                const deliveredMap = await fetchByDay(AMARON_DAILY.serviceBooked, deliveredFilter);

                const allDates = Array.from(
                    new Set([
                        ...totalLeadsMap.keys(),
                        ...newLeadsMap.keys(),
                        ...connectedMap.keys(),
                        ...interestedMap.keys(),
                        ...bookedMap.keys(),
                        ...deliveredMap.keys(),
                    ]),
                ).sort();

                return dataResult({
                    columnMetadata: [
                        {
                            id: colIds.date,
                            columnIndex: 0,
                            displayName: "Date",
                            dataType: DatasetColumnDataType.date,
                            role: DatasetColumnRole.dimension,
                        },
                        {
                            id: colIds.totalLeads,
                            columnIndex: 1,
                            displayName: "Total Leads",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.contacted,
                            columnIndex: 2,
                            displayName: "Contacted",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.contactedPct,
                            columnIndex: 3,
                            displayName: "Contacted (%)",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.connected,
                            columnIndex: 4,
                            displayName: "Connected",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.connectivityPct,
                            columnIndex: 5,
                            displayName: "Connectivity (%)",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.interested,
                            columnIndex: 6,
                            displayName: "Interested",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.booked,
                            columnIndex: 7,
                            displayName: "Bookings",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.crPct,
                            columnIndex: 8,
                            displayName: "Lead to booking (%)",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                        {
                            id: colIds.delivered,
                            columnIndex: 9,
                            displayName: "Delivered",
                            dataType: DatasetColumnDataType.number,
                            role: DatasetColumnRole.metric,
                        },
                    ],
                    dataPoints: allDates.map((date) => {
                        const leads = totalLeadsMap.get(date) ?? 0;
                        const newLeads = newLeadsMap.get(date) ?? 0;
                        const contacted = Math.max(0, leads - newLeads);
                        const connected = connectedMap.get(date) ?? 0;
                        const interested = interestedMap.get(date) ?? 0;
                        const booked = bookedMap.get(date) ?? 0;
                        const delivered = deliveredMap.get(date) ?? 0;
                        const contactedPct = amaronPercentage(contacted, leads);
                        const connectivityPct = amaronPercentage(connected, leads);
                        const crPct = amaronPercentage(booked, leads);
                        return [
                            {value: amaronDateOnlyLabel(date)},
                            {value: leads},
                            {value: contacted},
                            {value: contactedPct},
                            {value: connected},
                            {value: connectivityPct},
                            {value: interested},
                            {value: booked},
                            {value: crPct},
                            {value: delivered},
                        ];
                    }),
                } satisfies RawData);
            },
            timeZone,
            hideDateRangeFilter: true,
            hideKebabMenu: true,
            queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
        };
    };

    /*
    const makeTabbedTimeSeriesLineChartWidget = ({
        id,
        title,
        metrics,
        metricTabs,
    }: {
        id: Uuid;
        title: string | null;
        metrics: Uuid[];
        metricTabs: Array<{
            tabId: string;
            label: string;
            primaryMetricId: Uuid;
            secondaryMetricId?: Uuid;
            derivedSecondary?: {
                numeratorMetricId: Uuid;
                denominatorMetricId: Uuid;
                multiplyBy: number;
                label: string;
            };
            seriesColors?: {primary: string; secondary: string};
        }>;
    }): Widget => ({
        id,
        type: WidgetType.tabbedTimeSeriesLineChart,
        datasetId: amaronDailyPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        metricTabs,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
        dismissChartHoverTooltipOnPointerLeave: true,
    });
    */

    // ─────────────────────────────────────────────────────────────────────────

    return {
        id: uniqueUuid`a0d86456-e652-4dc8-ae59-b5ca123e3c7d`,
        title: "Performance Dashboard",
        sections: [
            // ═══════════════════════════════════════════════════════════════
            // SECTION 1 — Drop-off & Conversion Funnel Tracker
            // ═══════════════════════════════════════════════════════════════
            {
                id: uniqueUuid`aa110001-1100-4110-8110-110011001101`,
                title: "Drop-off & Conversion Funnel Tracker",
                rows: [
                    // ── Row 1: KPI snapshot ───────────────────────────────
                    {
                        title: null,
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTileWidget(uniqueUuid`ab100001-1000-4100-8100-100010001001`, "Total Leads", [
                                AMARON.totalLeads,
                            ]),
                            makeNumberTileWidget(uniqueUuid`ab100002-1000-4100-8100-100010001002`, "Enquiries", [
                                AMARON.enquiries,
                            ]),
                            makeNumberTileWidget(uniqueUuid`ab100003-1000-4100-8100-100010001003`, "Calls", [
                                AMARON.calls,
                            ]),
                            makeNumberTileWidget(uniqueUuid`ab100004-1000-4100-8100-100010001004`, "Service Booked", [
                                AMARON.serviceBooked,
                            ]),
                            makeNumberTileWidget(
                                uniqueUuid`ab100009-1000-4100-8100-100010001009`,
                                "Service Delivered",
                                [AMARON.serviceBooked],
                                "",
                                0,
                                [
                                    {
                                        type: FilterType.stringIn,
                                        attribute: AMARON.category,
                                        values: [AmaronServiceCategoryEnum.DELIVERED],
                                    },
                                ],
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`ab100010-1000-4100-8100-100010001010`,
                                "Service Cancelled",
                                [AMARON.serviceBooked],
                                "",
                                0,
                                [
                                    {
                                        type: FilterType.stringIn,
                                        attribute: AMARON.category,
                                        values: [AmaronServiceCategoryEnum.CANCELLED],
                                    },
                                ],
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`ab100006-1000-4100-8100-100010001006`,
                                "Lead → Calls",
                                [AMARON.leadToCallsPercent, AMARON.totalLeads, AMARON.calls],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`ab100008-1000-4100-8100-100010001008`,
                                "Lead → Service Booked",
                                [AMARON.leadToServiceBookedPercent, AMARON.totalLeads, AMARON.serviceBooked],
                                "%",
                                1,
                            ),
                        ],
                    },

                    // ── Row 2: End-to-End Pipeline Funnel ─────────────────
                    {
                        title: "Lead to Conversion Funnel — End-to-End Pipeline View",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeMetricAndDispositionFunnelWidget({
                                id: uniqueUuid`ab120001-1200-4120-8120-120012001201`,
                                title: null,
                                stages: [
                                    {
                                        metricId: uniqueUuid`ab120101-1201-4120-8120-120012001201`,
                                        label: "Total Leads",
                                        sourceMetricId: AMARON.totalLeads,
                                    },
                                    {
                                        metricId: uniqueUuid`ab120107-1201-4120-8120-120012001207`,
                                        label: "Total Leads Contacted",
                                        sourceMetricId: AMARON.totalLeads,
                                        excludedDispositionIds: AMARON_DISPOSITION_TILE_IDS.newLead,
                                    },
                                    {
                                        metricId: uniqueUuid`ab120102-1201-4120-8120-120012001202`,
                                        label: "Connected",
                                        sourceMetricId: AMARON.totalLeads,
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.connected,
                                    },
                                    {
                                        metricId: uniqueUuid`ab120103-1201-4120-8120-120012001203`,
                                        label: "Valid",
                                        sourceMetricId: AMARON.totalLeads,
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.validLead,
                                    },
                                    {
                                        metricId: uniqueUuid`ab120104-1201-4120-8120-120012001204`,
                                        label: "Interested",
                                        sourceMetricId: AMARON.totalLeads,
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.interested,
                                    },
                                    {
                                        metricId: uniqueUuid`ab120105-1201-4120-8120-120012001205`,
                                        label: "Booked",
                                        sourceMetricId: AMARON.serviceBooked,
                                    },
                                    {
                                        metricId: uniqueUuid`ab120106-1201-4120-8120-120012001206`,
                                        label: "Delivered",
                                        sourceMetricId: AMARON.serviceBooked,
                                        categoryIds: [AmaronServiceCategoryEnum.DELIVERED as Uuid],
                                    },
                                ],
                                showValues: true,
                                showConversionRate: false,
                                showDropOffRate: false,
                                funnelLayout: "labeledLeft",
                            }),
                        ],
                    },

                    (() => {
                        const tileIds = {
                            row: uniqueUuid`ab550000-5500-4550-8550-550055005500`,
                            totalLeads: uniqueUuid`ab550001-5500-4550-8550-550055005501`,
                            connected: uniqueUuid`ab550002-5500-4550-8550-550055005502`,
                            notConnected: uniqueUuid`ab550003-5500-4550-8550-550055005503`,
                            notInterested: uniqueUuid`ab550004-5500-4550-8550-550055005504`,
                            unserviceable: uniqueUuid`ab550005-5500-4550-8550-550055005505`,
                            nr1: uniqueUuid`ab550006-5500-4550-8550-550055005506`,
                            priceIssue: uniqueUuid`ab550007-5500-4550-8550-550055005507`,
                            areaNotCovered: uniqueUuid`ab550008-5500-4550-8550-550055005508`,
                            nr2: uniqueUuid`ab550009-5500-4550-8550-550055005509`,
                            serviceNotDue: uniqueUuid`ab550010-5500-4550-8550-550055005510`,
                            carNotSupported: uniqueUuid`ab550011-5500-4550-8550-550055005511`,
                            nr3: uniqueUuid`ab550012-5500-4550-8550-550055005512`,
                            oemPreferred: uniqueUuid`ab550013-5500-4550-8550-550055005513`,
                            immediateService: uniqueUuid`ab550014-5500-4550-8550-550055005514`,
                            nr4: uniqueUuid`ab550015-5500-4550-8550-550055005515`,
                            newCar: uniqueUuid`ab550016-5500-4550-8550-550055005516`,
                            rsaRequest: uniqueUuid`ab550017-5500-4550-8550-550055005517`,
                            nr5: uniqueUuid`ab550018-5500-4550-8550-550055005518`,
                            noRequirementShared: uniqueUuid`ab550019-5500-4550-8550-550055005519`,
                            noCarDetails: uniqueUuid`ab550020-5500-4550-8550-550055005520`,
                            nr6: uniqueUuid`ab550021-5500-4550-8550-550055005521`,
                            generalQuery: uniqueUuid`ab550022-5500-4550-8550-550055005522`,
                        };

                        const tiles = {
                            totalLeads: makeNumberTileWidget(tileIds.totalLeads, "Total Leads", [AMARON.totalLeads]),
                            connected: makeDispositionTile(
                                tileIds.connected,
                                "Connected",
                                AMARON_DISPOSITION_TILE_IDS.connected,
                            ),
                            notConnected: makeDispositionTile(
                                tileIds.notConnected,
                                "Not Connected",
                                AMARON_DISPOSITION_TILE_IDS.notConnected,
                            ),
                            notInterested: makeDispositionTile(
                                tileIds.notInterested,
                                "Not Interested",
                                AMARON_DISPOSITION_TILE_IDS.notInterested,
                            ),
                            unserviceable: makeDispositionTile(
                                tileIds.unserviceable,
                                "Unserviceable",
                                AMARON_DISPOSITION_TILE_IDS.unserviceable,
                            ),
                            nr1: makeDispositionTile(tileIds.nr1, "NR1", AMARON_DISPOSITION_TILE_IDS.nr1),
                            priceIssue: makeDispositionTile(
                                tileIds.priceIssue,
                                "Price Issue",
                                AMARON_DISPOSITION_TILE_IDS.priceIssue,
                            ),
                            areaNotCovered: makeDispositionTile(
                                tileIds.areaNotCovered,
                                "Area Not Covered",
                                AMARON_DISPOSITION_TILE_IDS.areaNotCovered,
                            ),
                            nr2: makeDispositionTile(tileIds.nr2, "NR2", AMARON_DISPOSITION_TILE_IDS.nr2),
                            serviceNotDue: makeDispositionTile(
                                tileIds.serviceNotDue,
                                "Service Not Due",
                                AMARON_DISPOSITION_TILE_IDS.serviceNotDue,
                            ),
                            carNotSupported: makeDispositionTile(
                                tileIds.carNotSupported,
                                "Car Not Supported",
                                AMARON_DISPOSITION_TILE_IDS.carNotSupported,
                            ),
                            nr3: makeDispositionTile(tileIds.nr3, "NR3", AMARON_DISPOSITION_TILE_IDS.nr3),
                            oemPreferred: makeDispositionTile(
                                tileIds.oemPreferred,
                                "OEM Preferred",
                                AMARON_DISPOSITION_TILE_IDS.oemPreferred,
                            ),
                            immediateService: makeDispositionTile(
                                tileIds.immediateService,
                                "Immediate Service",
                                AMARON_DISPOSITION_TILE_IDS.immediateService,
                            ),
                            nr4: makeDispositionTile(tileIds.nr4, "NR4", AMARON_DISPOSITION_TILE_IDS.nr4),
                            newCar: makeDispositionTile(tileIds.newCar, "New Car", AMARON_DISPOSITION_TILE_IDS.newCar),
                            rsaRequest: makeDispositionTile(
                                tileIds.rsaRequest,
                                "RSA Request",
                                AMARON_DISPOSITION_TILE_IDS.rsaRequest,
                            ),
                            nr5: makeDispositionTile(tileIds.nr5, "NR5", AMARON_DISPOSITION_TILE_IDS.nr5),
                            noRequirementShared: makeDispositionTile(
                                tileIds.noRequirementShared,
                                "No Requirement Shared",
                                AMARON_DISPOSITION_TILE_IDS.noRequirementShared,
                            ),
                            noCarDetails: makeDispositionTile(
                                tileIds.noCarDetails,
                                "No Car Details",
                                AMARON_DISPOSITION_TILE_IDS.noCarDetails,
                            ),
                            nr6: makeDispositionTile(tileIds.nr6, "NR6", AMARON_DISPOSITION_TILE_IDS.nr6),
                            generalQuery: makeDispositionTile(
                                tileIds.generalQuery,
                                "General Query",
                                AMARON_DISPOSITION_TILE_IDS.generalQuery,
                            ),
                        };

                        return {
                            id: tileIds.row,
                            title: "Negative Funnel Analysis",
                            type: DashboardRowType.customNumberTileRow,
                            gridConfig: {
                                columns: 4,
                                rows: [
                                    [{width: 4, widget: tiles.totalLeads}],
                                    [
                                        {width: 2, widget: tiles.connected},
                                        {width: 2, widget: tiles.notConnected},
                                    ],
                                    [
                                        {width: 1, widget: tiles.notInterested},
                                        {width: 1, widget: tiles.unserviceable},
                                        {width: 2, widget: tiles.nr1},
                                    ],
                                    [
                                        {width: 1, widget: tiles.priceIssue},
                                        {width: 1, widget: tiles.areaNotCovered},
                                        {width: 2, widget: tiles.nr2},
                                    ],
                                    [
                                        {width: 1, widget: tiles.serviceNotDue},
                                        {width: 1, widget: tiles.carNotSupported},
                                        {width: 2, widget: tiles.nr3},
                                    ],
                                    [
                                        {width: 1, widget: tiles.oemPreferred},
                                        {width: 1, widget: tiles.immediateService},
                                        {width: 2, widget: tiles.nr4},
                                    ],
                                    [
                                        {width: 1, widget: tiles.newCar},
                                        {width: 1, widget: tiles.rsaRequest},
                                        {width: 2, widget: tiles.nr5},
                                    ],
                                    [
                                        {width: 1, widget: tiles.noRequirementShared},
                                        {width: 1, widget: tiles.noCarDetails},
                                        {width: 2, widget: tiles.nr6},
                                    ],
                                    [{width: 1, widget: tiles.generalQuery}],
                                ],
                            },
                        };
                    })(),

                    // ── Row 3: Booking Time Slot Heatmap ─────────────────
                    {
                        title: "Booking Time Slot vs Day of Week",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeBookingTimeSlotWeekdayHeatMapWidget({
                                id: uniqueUuid`ab450006-4500-4450-8450-450045004506`,
                                title: null,
                            }),
                        ],
                    },

                    // ── Row 4: Source-wise Leads and Bookings ──────────────
                    // Uses UTM Source dimension to show Lead → Service Booked
                    // volume broken down by traffic source.
                    {
                        title: "Source-wise Leads and Bookings",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`ab125001-1250-4125-8125-125012501251`,
                                WidgetType.multiBarGraph,
                                null,
                                [AMARON.totalLeads, AMARON.serviceBooked],
                                [AMARON.utmSource],
                            ),
                        ],
                    },

                    // ── Row 4: Daily Trend ────────────────────────────────
                    {
                        title: "Bookings & Deliveries — Daily Trend Over Time",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            (() => {
                                const dailyTrendColIds = {
                                    date: uniqueUuid`ab130101-1301-4130-8130-130013001311`,
                                    booked: uniqueUuid`ab130103-1301-4130-8130-130013001313`,
                                    delivered: uniqueUuid`ab130104-1301-4130-8130-130013001314`,
                                };
                                const deliveredCategoryFilter: Filter = {
                                    type: FilterType.stringIn,
                                    attribute: AMARON_DAILY.category,
                                    values: [AmaronServiceCategoryEnum.DELIVERED as Uuid],
                                };
                                return {
                                    id: uniqueUuid`ab130001-1300-4130-8130-130013001301`,
                                    type: WidgetType.lineGraph,
                                    datasetId: amaronDailyPerformanceDataset.id,
                                    datasetResolver: partialGetDataset,
                                    title: null,
                                    startDate: startTime,
                                    endDate: endTime,
                                    dimensions: [],
                                    metrics: Object.values(dailyTrendColIds).filter(
                                        (id) => id !== dailyTrendColIds.date,
                                    ),
                                    externalFilters: [],
                                    editTimeFilters: [],
                                    viewTimeFilters: [],
                                    sortConfiguration: [],
                                    dataResolver: async (dataset, props) => {
                                        const metricTemplate = props.metrics[0];
                                        if (metricTemplate === undefined) {
                                            return dataResult({columnMetadata: [], dataPoints: []} satisfies RawData);
                                        }
                                        const fetchByDay = async (
                                            metricId: Uuid,
                                            extraFilter?: Filter,
                                        ): Promise<Map<string, number>> => {
                                            const filters: Filter =
                                                extraFilter !== undefined
                                                    ? {type: FilterType.and, predicates: [props.filters, extraFilter]}
                                                    : props.filters;
                                            const result = await partialFetchData(dataset, {
                                                ...props,
                                                dimensions: [],
                                                metrics: [{...metricTemplate, id: metricId}],
                                                filters,
                                            } as DataResolverProps);
                                            if (result.success !== true) {
                                                return new Map();
                                            }
                                            const timeCol = result.data.columnMetadata.find(
                                                (c) => c.role === DatasetColumnRole.dimension,
                                            );
                                            const metricCol = result.data.columnMetadata.find(
                                                (c) => c.id === metricId,
                                            );
                                            if (timeCol === undefined || metricCol === undefined) {
                                                return new Map();
                                            }
                                            const map = new Map<string, number>();
                                            for (const row of result.data.dataPoints) {
                                                const dateKey = String(row[timeCol.columnIndex]?.value ?? "");
                                                const val = row[metricCol.columnIndex]?.value;
                                                const num =
                                                    typeof val === "number" && Number.isFinite(val) ? val : 0;
                                                map.set(dateKey, (map.get(dateKey) ?? 0) + num);
                                            }
                                            return map;
                                        };
                                        const bookedMap = await fetchByDay(AMARON_DAILY.serviceBooked);
                                        const deliveredMap = await fetchByDay(
                                            AMARON_DAILY.serviceBooked,
                                            deliveredCategoryFilter,
                                        );
                                        const allDates = Array.from(
                                            new Set([
                                                ...bookedMap.keys(),
                                                ...deliveredMap.keys(),
                                            ]),
                                        ).sort();
                                        return dataResult({
                                            columnMetadata: [
                                                {
                                                    id: dailyTrendColIds.date,
                                                    columnIndex: 0,
                                                    displayName: "Date",
                                                    dataType: DatasetColumnDataType.dateTime,
                                                    role: DatasetColumnRole.dimension,
                                                },
                                                {
                                                    id: dailyTrendColIds.booked,
                                                    columnIndex: 1,
                                                    displayName: "Booked",
                                                    dataType: DatasetColumnDataType.number,
                                                    role: DatasetColumnRole.metric,
                                                },
                                                {
                                                    id: dailyTrendColIds.delivered,
                                                    columnIndex: 2,
                                                    displayName: "Delivered",
                                                    dataType: DatasetColumnDataType.number,
                                                    role: DatasetColumnRole.metric,
                                                },
                                            ],
                                            dataPoints: allDates.map((date) => [
                                                {value: date},
                                                {value: bookedMap.get(date) ?? 0},
                                                {value: deliveredMap.get(date) ?? 0},
                                            ]),
                                        } satisfies RawData);
                                    },
                                    timeZone,
                                    hideDateRangeFilter: true,
                                    hideKebabMenu: true,
                                    queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
                                    dismissChartHoverTooltipOnPointerLeave: true,
                                } satisfies Widget;
                            })(),
                        ],
                    },

                    // ── Row 5: Not Connected Attempts ─────────────────────
                    {
                        title: "Not Connected — NR Attempts",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeDispositionFilteredHorizontalBarGraphWidget({
                                id: uniqueUuid`ab140001-1400-4140-8140-140014001401`,
                                title: null,
                                stages: [
                                    {
                                        metricId: uniqueUuid`ab140101-1401-4140-8140-140014001401`,
                                        label: "Not Connected",
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.notConnected,
                                    },
                                    {
                                        metricId: uniqueUuid`ab140102-1401-4140-8140-140014001402`,
                                        label: "NR1",
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.nr1,
                                    },
                                    {
                                        metricId: uniqueUuid`ab140103-1401-4140-8140-140014001403`,
                                        label: "NR2",
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.nr2,
                                    },
                                    {
                                        metricId: uniqueUuid`ab140104-1401-4140-8140-140014001404`,
                                        label: "NR3",
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.nr3,
                                    },
                                    {
                                        metricId: uniqueUuid`ab140105-1401-4140-8140-140014001405`,
                                        label: "NR4",
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.nr4,
                                    },
                                    {
                                        metricId: uniqueUuid`ab140106-1401-4140-8140-140014001406`,
                                        label: "NR5",
                                        dispositionIds: AMARON_DISPOSITION_TILE_IDS.nr5,
                                    },
                                ],
                            }),
                        ],
                    },

                    // // ── Row 6: Lead Volume vs CR% ─────────────────────────
                    // {
                    //     title: "Lead Volume vs Conversion Rate % — Combined Trend",
                    //     type: DashboardRowType.chartsRow,
                    //     widgets: [
                    //         makeDailyChartWidget(
                    //             uniqueUuid`ab150001-1500-4150-8150-150015001501`,
                    //             WidgetType.comboChart,
                    //             null,
                    //             [AMARON_DAILY.totalLeads, AMARON_DAILY.serviceBooked],
                    //             [],
                    //         ),
                    //     ],
                    // },

                    // // ── Row 7: Lead Volume vs Connectivity ────────────────
                    // {
                    //     title: "Lead Volume vs Agent Connectivity Rate — Combined Trend",
                    //     type: DashboardRowType.chartsRow,
                    //     widgets: [
                    //         makeDailyChartWidget(
                    //             uniqueUuid`ab160001-1600-4160-8160-160016001601`,
                    //             WidgetType.comboChart,
                    //             null,
                    //             [AMARON_DAILY.totalLeads, AMARON_DAILY.calls],
                    //             [],
                    //         ),
                    //     ],
                    // },

                    /*
                    // ── Row 8: Drop-off alert tiles ───────────────────────
                    {
                        title: "Alerts & Exception Monitoring",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTileWidget(
                                uniqueUuid`ab170001-1700-4170-8170-170017001701`,
                                "Enquiry → Calls Drop-off %",
                                [AMARON.enquiryToCallsDropOffPercent, AMARON.enquiries, AMARON.calls],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`ab170002-1700-4170-8170-170017001702`,
                                "Leads Not Contacted %",
                                [AMARON.leadToCallsDropOffPercent, AMARON.totalLeads, AMARON.calls],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`ab170003-1700-4170-8170-170017001703`,
                                "Calls → Service Booked Drop-off %",
                                [AMARON.callsToServiceBookedDropOffPercent, AMARON.calls, AMARON.serviceBooked],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`ab170004-1700-4170-8170-170017001704`,
                                "Lead → Service Booked Drop-off %",
                                [AMARON.leadToServiceBookedDropOffPercent, AMARON.totalLeads, AMARON.serviceBooked],
                                "%",
                                1,
                            ),
                        ],
                    },
                    */
                ],
            },

            // ═══════════════════════════════════════════════════════════════
            // SECTION 2 — Agent and Source Performance
            // ═══════════════════════════════════════════════════════════════
            {
                id: uniqueUuid`aa220002-2200-4220-8220-220022002202`,
                title: "Agent and Source Performance",
                rows: [
                    // ── Row 1: Agent-wise Performance ─────────────────────
                    {
                        title: "Agent-wise Performance",
                        type: DashboardRowType.tableRow,
                        widgets: [
                            makeAgentPerformanceTableWidget(uniqueUuid`ab210001-2100-4210-8210-210021002101`, null),
                        ],
                    },

                    // ── Row 2: Source-wise Day-on-Day Leads Breakdown ─────
                    {
                        title: "Source-wise Day-on-Day Leads Breakdown — All Lead Sources",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeDailyChartWidget(
                                uniqueUuid`ab220001-2200-4220-8220-220022002201`,
                                WidgetType.stackedBarGraph,
                                null,
                                [AMARON_DAILY.totalLeads],
                                [AMARON_DAILY.utmSource],
                                AnalyticsQueryVariant.periodicAsPeriodic,
                            ),
                        ],
                    },
                ],
            },

            // ═══════════════════════════════════════════════════════════════
            // SECTION 3 — Disposition Analysis View
            // ═══════════════════════════════════════════════════════════════
            {
                id: uniqueUuid`aa330003-3300-4330-8330-330033003303`,
                title: "Disposition Analysis View",
                rows: [
                    // ── Row 1: Day-on-Day Disposition Table ───────────────
                    {
                        title: "Day-on-Day Funnel Movement — Disposition Tracker",
                        type: DashboardRowType.tableRow,
                        widgets: [
                            makeDailyDispositionTrackerTableWidget(
                                uniqueUuid`ab320001-3200-4320-8320-320032003201`,
                                null,
                            ),
                        ],
                    },

                    /*
                    // ── Row 2: Disposition Ladder ─────────────────────────
                    {
                        title: "Lead Status Split",
                        type: DashboardRowType.ladderSection,
                        levelSizes: [1, 3, 4, 6, 6],
                        widgets: [
                            makeDispositionTile(
                                uniqueUuid`ab330101-3301-4331-8331-330133013301`,
                                "New Lead",
                                AMARON_DISPOSITION_TILE_IDS.newLead,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330102-3301-4331-8331-330133013302`,
                                "Connected",
                                AMARON_DISPOSITION_TILE_IDS.connected,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330103-3301-4331-8331-330133013303`,
                                "Not Connected",
                                AMARON_DISPOSITION_TILE_IDS.notConnected,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330104-3301-4331-8331-330133013304`,
                                "Wrong Number",
                                AMARON_DISPOSITION_TILE_IDS.wrongNumber,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330105-3301-4331-8331-330133013305`,
                                "Valid Lead",
                                AMARON_DISPOSITION_TILE_IDS.validLead,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330106-3301-4331-8331-330133013306`,
                                "Call Back Later",
                                AMARON_DISPOSITION_TILE_IDS.callBackLater,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330107-3301-4331-8331-330133013307`,
                                "Invalid Lead",
                                AMARON_DISPOSITION_TILE_IDS.invalidLead,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330108-3301-4331-8331-330133013308`,
                                "Not Reachable",
                                AMARON_DISPOSITION_TILE_IDS.notReachable,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330109-3301-4331-8331-330133013309`,
                                "Interested",
                                AMARON_DISPOSITION_TILE_IDS.interested,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330110-3301-4331-8331-330133013310`,
                                "Not Interested",
                                AMARON_DISPOSITION_TILE_IDS.notInterested,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330111-3301-4331-8331-330133013311`,
                                "Unserviceable",
                                AMARON_DISPOSITION_TILE_IDS.unserviceable,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330112-3301-4331-8331-330133013312`,
                                "Call Hangup",
                                AMARON_DISPOSITION_TILE_IDS.callHangup,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330113-3301-4331-8331-330133013313`,
                                "Did Not Fill",
                                AMARON_DISPOSITION_TILE_IDS.didNotFill,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330114-3301-4331-8331-330133013314`,
                                "Filled By Mistake",
                                AMARON_DISPOSITION_TILE_IDS.filledByMistake,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330115-3301-4331-8331-330133013315`,
                                "Follow Up",
                                AMARON_DISPOSITION_TILE_IDS.followUp,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330116-3301-4331-8331-330133013316`,
                                "Need More Information",
                                AMARON_DISPOSITION_TILE_IDS.needMoreInformation,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330117-3301-4331-8331-330133013317`,
                                "Booked",
                                AMARON_DISPOSITION_TILE_IDS.booked,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330118-3301-4331-8331-330133013318`,
                                "Other",
                                AMARON_DISPOSITION_TILE_IDS.other,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330119-3301-4331-8331-330133013319`,
                                "Not Interested Reasons",
                                AMARON_DISPOSITION_TILE_IDS.notInterestedReasons,
                            ),
                            makeDispositionTile(
                                uniqueUuid`ab330120-3301-4331-8331-330133013320`,
                                "Unserviceable Reasons",
                                AMARON_DISPOSITION_TILE_IDS.unserviceableReasons,
                            ),
                        ],
                    },
                    */

                    /*
                    // ── Row 3: Call Outcome Distribution ──────────────────
                    {
                        title: "Call Outcome Distribution — Disposition Breakdown",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeChartWidget(
                                uniqueUuid`ab340001-3400-4340-8340-340034003401`,
                                WidgetType.horizontalBarGraph,
                                null,
                                [AMARON.totalLeads, AMARON.calls, AMARON.serviceBooked],
                                [AMARON.disposition],
                            ),
                        ],
                    },
                    */
                ],
            },

            // ═══════════════════════════════════════════════════════════════
            // SECTION 4 — Vehicle and Service Intelligence
            // ═══════════════════════════════════════════════════════════════
            {
                id: uniqueUuid`aa440004-4400-4440-8440-440044004404`,
                title: "Vehicle and Service Intelligence",
                rows: [
                    // ── Row 1: Journey Created-at Slot Heatmap ───────────
                    {
                        title: "Service Booking Created 2-Hour Slot vs Day of Week",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeCreatedAtTwoHourWeekdayHeatMapWidget({
                                id: uniqueUuid`ab450001-4500-4450-8450-450045004501`,
                                title: null,
                                valueMetricId: AMARON_DAILY.serviceBooked,
                            }),
                        ],
                    },

                    // ── Row 2: Journey Created-at Slot Heatmap
                    {
                        title: "Leads Created 2-Hour Slot vs Day of Week",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeCreatedAtTwoHourWeekdayHeatMapWidget({
                                id: uniqueUuid`ab450004-4500-4450-8450-450045004504`,
                                title: null,
                                valueMetricId: AMARON_DAILY.totalLeads,
                            }),
                        ],
                    },

                    // ── Row 3: Car Make & Model ───────────────────────────
                    {
                        title: "Leads by Car Make and Model",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            // TODO: Add service booked metrics, when available
                            makeChartWidget(
                                uniqueUuid`ab410001-4100-4410-8410-410041004101`,
                                WidgetType.horizontalBarGraph,
                                "By Car Make",
                                [
                                    AMARON.totalLeads,
                                    // AMARON.calls,
                                    // AMARON.serviceBooked
                                ],
                                [AMARON.carMake],
                            ),
                        ],
                    },

                    // ── Row 4: Fuel Type Distribution ─────────────────────
                    {
                        title: "Lead & Booking Distribution by Fuel Type",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            /*
                            makeChartWidget(
                                uniqueUuid`ab420001-4200-4420-8420-420042004201`,
                                WidgetType.donutChart,
                                null,
                                [AMARON.totalLeads],
                                [AMARON.fuelType],
                                AnalyticsQueryVariant.periodicAsNonTimeSeries,
                                {
                                    dismissChartHoverTooltipOnPointerLeave: true,
                                    chartColorPalette: AMARON_DONUT_SLICE_COLORS,
                                },
                            ),
                            */
                            makeChartWidget(
                                uniqueUuid`ab420002-4200-4420-8420-420042004202`,
                                WidgetType.horizontalBarGraph,
                                null,
                                [AMARON.totalLeads, AMARON.serviceBooked],
                                [AMARON.fuelType],
                            ),
                        ],
                    },

                    // ── Row 5: Service Type Distribution ──────────────────
                    {
                        title: "Booking Split Across Service Types",
                        type: DashboardRowType.tableRow,
                        widgets: [
                            (() => {
                                const serviceTypesColIds = {
                                    serviceRequested: uniqueUuid`ab430101-4301-4430-8430-430043004311`,
                                    totalLeads: uniqueUuid`ab430102-4301-4430-8430-430043004312`,
                                    booked: uniqueUuid`ab430103-4301-4430-8430-430043004313`,
                                    delivered: uniqueUuid`ab430104-4301-4430-8430-430043004314`,
                                };
                                const deliveredCategoryFilter: Filter = {
                                    type: FilterType.stringIn,
                                    attribute: AMARON.category,
                                    values: [AmaronServiceCategoryEnum.DELIVERED as Uuid],
                                };
                                return {
                                    id: uniqueUuid`ab430001-4300-4430-8430-430043004301`,
                                    type: WidgetType.table,
                                    datasetId: amaronPerformanceDataset.id,
                                    datasetResolver: partialGetDataset,
                                    title: null,
                                    startDate: startTime,
                                    endDate: endTime,
                                    dimensions: [AMARON.serviceRequested],
                                    metrics: [
                                        serviceTypesColIds.totalLeads,
                                        serviceTypesColIds.booked,
                                        serviceTypesColIds.delivered,
                                    ],
                                    externalFilters: [],
                                    editTimeFilters: [],
                                    viewTimeFilters: [],
                                    sortConfiguration: [],
                                    dataResolver: async (dataset, props) => {
                                        const metricTemplate = props.metrics[0];
                                        if (metricTemplate === undefined) {
                                            return dataResult({columnMetadata: [], dataPoints: []} satisfies RawData);
                                        }

                                        const fetchByServiceType = async (
                                            metricId: Uuid,
                                            extraFilter?: Filter,
                                        ): Promise<Map<string, number>> => {
                                            const filters: Filter =
                                                extraFilter !== undefined
                                                    ? {type: FilterType.and, predicates: [props.filters, extraFilter]}
                                                    : props.filters;
                                            const result = await partialFetchData(dataset, {
                                                ...props,
                                                dimensions: [{id: AMARON.serviceRequested}],
                                                metrics: [{...metricTemplate, id: metricId}],
                                                filters,
                                            } as DataResolverProps);
                                            if (result.success !== true) {
                                                return new Map();
                                            }
                                            return readRawGroupedMetricValues(
                                                result.data,
                                                AMARON.serviceRequested,
                                                metricId,
                                            );
                                        };

                                        const totalLeadsMap = await fetchByServiceType(AMARON.totalLeads);
                                        const bookedMap = await fetchByServiceType(AMARON.serviceBooked);
                                        const deliveredMap = await fetchByServiceType(
                                            AMARON.serviceBooked,
                                            deliveredCategoryFilter,
                                        );

                                        const allServiceTypes = Array.from(
                                            new Set([
                                                ...totalLeadsMap.keys(),
                                                ...bookedMap.keys(),
                                                ...deliveredMap.keys(),
                                            ]),
                                        ).sort((a, b) => a.localeCompare(b));

                                        return dataResult({
                                            columnMetadata: [
                                                {
                                                    id: serviceTypesColIds.serviceRequested,
                                                    columnIndex: 0,
                                                    displayName: "Service Requested",
                                                    dataType: DatasetColumnDataType.string,
                                                    role: DatasetColumnRole.dimension,
                                                },
                                                {
                                                    id: serviceTypesColIds.totalLeads,
                                                    columnIndex: 1,
                                                    displayName: "Total Leads",
                                                    dataType: DatasetColumnDataType.number,
                                                    role: DatasetColumnRole.metric,
                                                },
                                                {
                                                    id: serviceTypesColIds.booked,
                                                    columnIndex: 2,
                                                    displayName: "Booked",
                                                    dataType: DatasetColumnDataType.number,
                                                    role: DatasetColumnRole.metric,
                                                },
                                                {
                                                    id: serviceTypesColIds.delivered,
                                                    columnIndex: 3,
                                                    displayName: "Delivered",
                                                    dataType: DatasetColumnDataType.number,
                                                    role: DatasetColumnRole.metric,
                                                },
                                            ],
                                            dataPoints: allServiceTypes.map((serviceType) => [
                                                {value: serviceType},
                                                {value: totalLeadsMap.get(serviceType) ?? 0},
                                                {value: bookedMap.get(serviceType) ?? 0},
                                                {value: deliveredMap.get(serviceType) ?? 0},
                                            ]),
                                        } satisfies RawData);
                                    },
                                    timeZone,
                                    hideDateRangeFilter: true,
                                    hideKebabMenu: true,
                                    queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
                                } satisfies Widget;
                            })(),
                        ],
                    },

                    /*
                    // ── Row 4: Car Make × Service Requested (daily trend) ─
                    {
                        title: "Car Make vs Service Requested — Daily Trend",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            // TODO: Add service booked metrics, when available
                            makeDailyChartWidget(
                                uniqueUuid`ab440001-4400-4440-8440-440044004401`,
                                WidgetType.stackedBarGraph,
                                null,
                                [
                                    AMARON_DAILY.totalLeads,
                                    // AMARON_DAILY.serviceBooked
                                ],
                                [
                                    AMARON_DAILY.carMake,
                                    // AMARON_DAILY.serviceRequested,
                                ],
                                AnalyticsQueryVariant.periodicAsNonTimeSeries,
                            ),
                        ],
                    },
                    */

                ],
            },
        ],
    };
}
